<template>
	<v-row>
		<v-col>
			<v-form v-model="formIsValid" ref="form">

				<ui-box new-forms>
					<form-title title="Order Confirmation Settings"></form-title>

					<form-divider inner/>

					<v-row>
						<v-col>
							<v-textarea
									v-model="channel.confirmation_page_message"
									auto-grow
									class="rounded-lg"
									counter="250"
									hint="This is the message show to the customer on the purchase confirmation page."
									label="Purchase Confirmation Message"
									outlined
									persistent-hint
									rows="4"
									@input="setDirty"
							></v-textarea>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<v-text-field
									v-model="channel.emails.receipt.subject"
									class="rounded-lg"
									hint="This is the subject of the receipt email."
									label="Receipt Email Subject"
									outlined
									persistent-hint
									@input="setDirty"
							></v-text-field>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<v-textarea
									v-model="channel.texts.purchase.content"
									auto-grow
									class="rounded-lg"
									counter="250"
									hint="This is the text message a customer will receive after purchase. Leave blank to not send."
									label="Text Message on Purchase"
									outlined
									persistent-hint
									rows="4"
									@input="setDirty"
							></v-textarea>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<v-textarea
									v-model="channel.texts.gift.content"
									auto-grow
									class="rounded-lg"
									counter="250"
									hint="This is the text message that is sent when a purchase is gifted. Leave blank to not send."
									label="Text Message on Gift"
									outlined
									persistent-hint
									rows="4"
									@input="setDirty"
							></v-textarea>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<v-textarea
									v-model="channel.texts.transfer.content"
									auto-grow
									class="rounded-lg"
									counter="250"
									hint="This is the text message that is sent when an item is transferred to someone else. Leave blank to not send."
									label="Text Message on Transfer"
									outlined
									persistent-hint
									rows="4"
									@input="setDirty"
							></v-textarea>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<v-switch
									v-model="channel.texts.reward.send"
									class="mt-3"
									dense
									:false-value="0"
									:true-value="1"
									hide-details="auto"
									inset
									label="Send Text Message on Reward Claim?"
							></v-switch>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-textarea
									v-model="channel.texts.reward.content"
									auto-grow
									class="rounded-lg"
									counter="250"
									hint="This is the text message that is sent when a prize is claimed. Leave blank to not send."
									label="Text Message on Claimed Reward"
									outlined
									persistent-hint
									rows="4"
									@input="setDirty"
							></v-textarea>
						</v-col>
					</v-row>
				</ui-box>

				<ui-box>
					<div class="d-flex align-center secondary--text font-weight-bold mb-0">
						<v-icon size="16" class="mr-1 secondary--text font-weight-bold">
							mdi-lightbulb-alert-outline
						</v-icon>
						<span>Hint:</span>
					</div>
					<p class="font-weight-bold">Use these variables inside your text messages to
						display the corresponding
						values</p>

					<form-divider inner/>

					<span v-pre>{{customerName}}</span> - The full name of the customer.<br/>
					<span v-pre>{{partnerName}}</span> - The name of the partner.<br/>
					<span v-pre>{{dashboardUrl}}</span> - A link to the customer's dashboard.<br/>
					<span v-pre>{{masterVoucher}}</span> - The master voucher. (purchase text
					message only)<br/>
					<span v-pre>{{individualVoucher}}</span> - The individual voucher for the shared
					or transferred deal. (share/transfer text message only)<br/>
					<span v-pre>{{giftMessage}}</span> - The message someone leaves when they are
					gifting the order to someone.<br/>
				</ui-box>

				<save-form
						:refs="$refs"
						:is-saving="isSaving"
						:is-valid="formIsValid"
						new-forms
						save-text="Save"
						@cancel-action="cancelChanges"
						@save-action="saveChanges"
				></save-form>

				<confirm-dialog
						description="Your changes have not been saved. Leaving now will discard them. Leave blank to not send."
						:open="exitDialog"
						title="Unsaved Changes"
						@cancel="() => exitDialog = false"
						@confirm="doCancelChanges"
				></confirm-dialog>

			</v-form>
		</v-col>
	</v-row>
</template>

<script>
import ConfirmDialog from '@app/Shared/UI/ConfirmDialog.vue';

export default {
	name: 'ChannelCommunication',
	components: {
		ConfirmDialog
	},
	props: {
		channel: {
			type: Object,
			default: () => ({})
		},
		partner: {
			type: Object | null,
			default: null
		},
		returnUrl: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			exitDialog: false,
			formIsValid: false,
			isDirty: false,
			isSaving: false
		}
	},
	created() {
		this.setDefaultValues(this.channel);
	},
	methods: {
		cancelChanges() {
			if(this.isDirty) {
				this.exitDialog = true;
			}
			else {
				this.doCancelChanges();
			}
		},
		doCancelChanges() {
			window.location = this.getReturnUrl();
		},
		getReturnUrl() {
			return `/partner-portal/${this.partner.id}/channels/${this.channel.id}/faqs`;
		},
		setDefaultValues(channel) {
			channel.confirmation_page_message = channel.confirmation_page_message || channel.const.DEFAULT_CONFIRMATION_PAGE_MESSAGE;

			channel.emails.receipt.subject = channel.emails.receipt.subject || channel.const.DEFAULT_EMAIL_RECEIPT_SUBJECT;

			channel.texts = channel.texts ? channel.texts : {};
			channel.texts.purchase = channel.texts.purchase && channel.texts.purchase?.content
					? channel.texts.purchase
					: { send: 1, content: channel.const.DEFAULT_PURCHASE_TEXT_MESSAGE };
			channel.texts.gift = channel.texts.gift && channel.texts.gift?.content
					? channel.texts.gift
					: { send: 1, content: channel.const.DEFAULT_GIFT_TEXT_MESSAGE };
			channel.texts.transfer = channel.texts.transfer && channel.texts.transfer?.content
					? channel.texts.transfer
					: { send: 1, content: channel.const.DEFAULT_TRANSFER_TEXT_MESSAGE };
			channel.texts.reward = channel.texts.reward && channel.texts.reward?.content
					? channel.texts.reward
					: channel.texts.reward?.send
							? {
								send: channel.texts.reward.send,
								content: channel.const.DEFAULT_REWARD_TEXT_MESSAGE
							}
							: { send: 1, content: channel.const.DEFAULT_REWARD_TEXT_MESSAGE };
		},
		setDirty() {
			this.isDirty = true;
		},
		saveChanges() {
			// Validate first...
			this.$refs.form.validate();
			if(!this.formIsValid) return;

			this.isSaving = true;

			// If they leave it blank, don't send.
			this.channel.texts.purchase.send = this.channel.texts.purchase.content ? 1 : 0;
			this.channel.texts.gift.send = this.channel.texts.gift.content ? 1 : 0;
			this.channel.texts.transfer.send = this.channel.texts.transfer.content ? 1 : 0;

			let url = `/partner-portal/${this.partner.id}/channels/save-channel/${this.channel.id || ''}`;
			axios.put(url, this.channel).then(response => {
				this.channel.id = response.data.channel.id;
				window.EventBus.$emit('system-messages', [
					{
						message: this.channel.name + ' ' + (this.channel.id
								? 'updated'
								: 'created') + ' - redirecting now...',
						messageType: 'success',
						showOverlay: true
					}
				]);
				setTimeout(() => {
					window.location = this.getReturnUrl();
				}, 2000);
			}).catch(error => {
				this.isSaving = false;
				window.EventBus.$emit('system-messages', [
					{ message: error.message, messageType: 'error' }
				]);
			});
		}
	}
};

</script>
